"use client";
import { AlertTriangle } from "lucide-react";
import { Meter as AriaMeter, MeterProps as AriaMeterProps } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { composeTailwindRenderProps } from "../../utils";
import { Label } from "../Field";

export interface MeterProps extends AriaMeterProps {
  /** Label displayed above the meter */
  label?: string;
  /** Size variant of the meter */
  size?: "sm" | "md" | "lg";
  /** Visual variant based on semantic meaning */
  variant?: "default" | "success" | "warning" | "error" | "info";
  /** Whether to show warning indicator at high values */
  showWarningIndicator?: boolean;
  /** Threshold percentage for showing warning (default: 80) */
  warningThreshold?: number;
  /** Additional CSS classes */
  className?: string;
}

const sizeStyles = {
  sm: {
    bar: "h-1.5 w-48",
    text: "text-xs",
    icon: "h-3 w-3",
  },
  md: {
    bar: "h-2 w-64",
    text: "text-sm",
    icon: "h-4 w-4",
  },
  lg: {
    bar: "h-3 w-80",
    text: "text-base",
    icon: "h-5 w-5",
  },
};

/**
 * Meter
 *
 * Displays a measurement within a known range, with visual indicators for different value ranges.
 * Perfect for showing capacity, usage levels, scores, or any bounded measurement.
 */
export function Meter({
  label,
  size = "md",
  variant = "default",
  showWarningIndicator = true,
  warningThreshold = 80,
  className,
  ...props
}: MeterProps) {
  const styles = sizeStyles[size];

  return (
    <AriaMeter {...props} className={composeTailwindRenderProps(className, "flex flex-col gap-1")}>
      {({ percentage, valueText }) => {
        const isWarning = showWarningIndicator && percentage >= warningThreshold;
        const effectiveVariant = variant === "default" ? getAutomaticVariant(percentage) : variant;

        return (
          <>
            <div className="flex justify-between gap-2">
              {label && <Label>{label}</Label>}
              <span className={twMerge(styles.text, isWarning ? "text-feedback-error-text" : "text-text-muted")}>
                {isWarning && (
                  <AlertTriangle
                    aria-label="Warning: High value"
                    className={twMerge("inline-block align-text-bottom mr-1", styles.icon)}
                  />
                )}
                {valueText}
              </span>
            </div>
            <div
              className={twMerge(
                "relative rounded-full bg-background-muted",
                "ring-1 ring-inset ring-border-muted",
                styles.bar
              )}
            >
              <div
                className={twMerge(
                  "absolute left-0 top-0 h-full rounded-full transition-all duration-300",
                  "forced-colors:bg-[Highlight]",
                  getMeterColor(effectiveVariant)
                )}
                style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
                role="presentation"
                aria-hidden="true"
              />
            </div>
          </>
        );
      }}
    </AriaMeter>
  );
}

/**
 * Automatically determine variant based on percentage
 */
function getAutomaticVariant(percentage: number): MeterProps["variant"] {
  if (percentage < 50) return "success";
  if (percentage < 70) return "info";
  if (percentage < 90) return "warning";
  return "error";
}

/**
 * Get the appropriate color class based on variant
 */
function getMeterColor(variant: MeterProps["variant"]) {
  switch (variant) {
    case "success":
      return "bg-feedback-success-text";
    case "warning":
      return "bg-feedback-warning-text";
    case "error":
      return "bg-feedback-error-text";
    case "info":
      return "bg-feedback-info-text";
    default:
      return "bg-action-brand";
  }
}
